import { AbsenceOptionUnit } from '@app/enums';
import { AbsenceStatus, BulkUpdateAbsenceRequest } from '@app/reducers/orm/absence/absence.model';
import { RosterActions } from '@app/reducers/orm/absence/roster-actions.enum';
import { AbsenteeOptionModel } from '@app/reducers/orm/absentee-option/absentee-option.model';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addDays, eachDayOfInterval } from 'date-fns';

import type { AbsenceDayEntry, AbsenceEntryPayload, AbsenceSlot } from './dev-tools-hydration.models';
import {
  getRandomElement,
  getRandomIntBetween,
  MOCK_ABSENCE_NOTES,
  randomWeightedValue,
  shuffleArray,
} from './dev-tools-mock-data';

/** Absences get one non-overlapping week slot per (user, week) across ±4 weeks around today. */
const ABSENCE_WEEK_OFFSETS = [-4, -3, -2, -1, 0, 1, 2, 3];

/**
 * Picks unique (employee, week) slots across ±4 weeks around today, each with a
 * 1-2 day absence inside its own week, so absences for one user never overlap.
 * Slots that would overlap a date in `occupiedDatesByUser` (existing absences)
 * are moved within their week or dropped, so the backend never rejects them
 * with "Already absent during this period". Capped at the number of available
 * combinations. Each slot gets a weighted status up front: approved slots are
 * bulk-created, the rest individually.
 */
export function generateAbsenceSlots(
  userIds: string[],
  count: number,
  occupiedDatesByUser: Map<string, Set<string>> = new Map(),
): AbsenceSlot[] {
  const candidates = ABSENCE_WEEK_OFFSETS.flatMap((weekOffset) => {
    // One preferred period per week: conflict-free users share it, which lets
    // the bulk endpoint create all their absences in a single request. The
    // current week prefers today, so "absent today" views always get data.
    const preferredDayOffset = weekOffset === 0 ? 0 : getRandomIntBetween(0, 5);
    const preferredDurationDays = getRandomIntBetween(0, 1);

    return userIds.flatMap((userId) => {
      const slot = allocateAbsenceSlot(
        userId,
        weekOffset,
        preferredDayOffset,
        preferredDurationDays,
        occupiedDatesByUser.get(userId) ?? new Set<string>(),
      );

      return slot ? [slot] : [];
    });
  });

  return shuffleArray(candidates).slice(0, count);
}

/**
 * Finds a period within the week that doesn't overlap the user's occupied dates:
 * the week's preferred period first, then single days as fallback. Returns null
 * when every day of the week is already taken.
 */
function allocateAbsenceSlot(
  userId: string,
  weekOffset: number,
  preferredDayOffset: number,
  preferredDurationDays: number,
  occupiedDates: Set<string>,
): AbsenceSlot | null {
  const periodsToTry = [
    { dayOffset: preferredDayOffset, durationDays: preferredDurationDays },
    ...Array.from({ length: 6 }, (unusedValue, dayOffset) => ({ dayOffset, durationDays: 0 })),
  ];

  for (const { dayOffset, durationDays } of periodsToTry) {
    const startDate = addDays(new Date(), weekOffset * 7 + dayOffset);
    const periodDates = Array.from({ length: durationDays + 1 }, (unusedValue, periodDayIndex) =>
      toISODateString(addDays(startDate, periodDayIndex)),
    );

    if (periodDates.every((periodDate) => !occupiedDates.has(periodDate))) {
      return {
        userId,
        startdate: periodDates[0],
        enddate: periodDates[periodDates.length - 1],
        weekOffset,
        status: randomWeightedValue([
          { value: AbsenceStatus.APPROVED, weight: 70 },
          { value: AbsenceStatus.PENDING, weight: 25 },
          { value: AbsenceStatus.DECLINED, weight: 5 },
        ]),
      };
    }
  }

  return null;
}

export function generateAbsence(
  slot: AbsenceSlot,
  absenteeOption: Pick<AbsenteeOptionModel, 'id' | 'unit'>,
): AbsenceEntryPayload {
  return {
    user_id: slot.userId,
    absentee_option_id: absenteeOption.id,
    startdate: slot.startdate,
    enddate: slot.enddate,
    status: slot.status,
    note: getRandomElement(MOCK_ABSENCE_NOTES),
    open_ended: false,
    wait_days: 0,
    hide_days_without_hours: false,
    notify_employee: false,
    roster_action: RosterActions.NONE,
    AbsenteeDay: generateAbsenceDays(slot, absenteeOption.unit),
  };
}

/**
 * Groups slots sharing the same period into POST /absentees/bulk payloads:
 * one period and absence type per request, covering every user in the group.
 * The slot periods are reused as-is — they were allocated against existing
 * absences, so inventing a new period here could reintroduce conflicts.
 * The backend forces these to Approved, so only pass approved slots in.
 */
export function generateBulkAbsences(
  slots: AbsenceSlot[],
  absenteeOptions: Pick<AbsenteeOptionModel, 'id' | 'unit'>[],
): BulkUpdateAbsenceRequest[] {
  const slotsByPeriod = new Map<string, AbsenceSlot[]>();
  slots.forEach((slot) => {
    const periodKey = `${slot.startdate}|${slot.enddate}`;
    slotsByPeriod.set(periodKey, [...(slotsByPeriod.get(periodKey) ?? []), slot]);
  });

  return [...slotsByPeriod.values()].map((periodSlots) => {
    const { startdate, enddate } = periodSlots[0];
    const absenteeOption = getRandomElement(absenteeOptions);
    const isDaysUnit = absenteeOption.unit === AbsenceOptionUnit.DAYS;
    const dayCount = eachDayOfInterval({ start: new Date(startdate), end: new Date(enddate) }).length;

    const payload: BulkUpdateAbsenceRequest = {
      Absentee: {
        absentee_option_id: absenteeOption.id,
        partial_day: false,
        startdate,
        // Days-unit bulk requests are single-day, mirroring the bulk add-absence
        // UI. Shrinking the checked period is safe — its dates stay conflict-free.
        enddate: isDaysUnit ? startdate : enddate,
        note: getRandomElement(MOCK_ABSENCE_NOTES),
        roster_action: RosterActions.NONE,
      },
    };

    if (isDaysUnit) {
      payload.UserDays = periodSlots.map((periodSlot) => ({ user_id: periodSlot.userId, days: 1 }));
    } else {
      payload.UserHours = periodSlots.map((periodSlot) => ({ user_id: periodSlot.userId, hours: 8 * dayCount }));
    }

    return payload;
  });
}

/**
 * Builds one entry per absence date. The backend expects `hours` for hours-unit
 * absence types and `days` for days-unit types, like the absence request form sends.
 */
function generateAbsenceDays(slot: AbsenceSlot, unit: AbsenceOptionUnit | undefined): AbsenceDayEntry[] {
  const absenceDates = eachDayOfInterval({ start: new Date(slot.startdate), end: new Date(slot.enddate) });

  return absenceDates.map((absenceDate) => {
    const date = toISODateString(absenceDate);

    if (unit === AbsenceOptionUnit.DAYS) {
      return { date, days: 1 };
    }

    return { date, hours: '8', wait_hours: '0.00000', start_time: '00:00:00' };
  });
}
